import type { AxiosInstance } from 'axios';

import { invertMap } from '@/sections/insights/queryBuilder/entities/common/invert';
import {
  packStringsForAPI,
  unpackStringsForUI,
} from '@/sections/insights/queryBuilder/entities/common/multiValueHelpers';
import {
  getNotFromSimpleFilter,
  getOperatorFromQueryFilter,
  getOperatorFromSimpleFilter,
  getValueFromQueryFilter,
  getValueFromSimpleFilter,
} from '@/sections/insights/queryBuilder/entities/common/translators';
import type {
  SimpleFilter,
  SimpleFilterDefinition,
} from '@/sections/insights/queryBuilder/entities/types';
import { parseTechnologyProductName } from '@/sections/technology/utils';
import type { QueryFilter } from '@/types';
import { formatPrettyName } from '@/utils/mappings.util';

// Module-level maps (populated by fetch)
let productDisplayMap: Record<string, string> = {};
let productDisplayMapInverse: Record<string, string> = {};

// Export for testing
export const __setProductMapsForTesting = (
  displayMap: Record<string, string>
) => {
  productDisplayMap = displayMap;
  productDisplayMapInverse = invertMap(displayMap);
};

/**
 * Fetch technology products from statistics endpoint
 * Uses the same statistics key as useGetTechnologyFilters hook
 */
async function fetchTechnologyProducts(
  axios: AxiosInstance,
  options?: { isGlobal?: boolean }
): Promise<string[]> {
  try {
    const response = await axios.get<{
      count: number;
      statistics: Array<{ value: string }>;
    }>('/my', {
      params: {
        label: 'statistic',
        key: '#statistic#technology#product#now#',
        ...(options?.isGlobal ? { allTenants: true } : {}),
      },
    });

    // Build display maps for raw API values → pretty names
    productDisplayMap = {};
    response.data.statistics.forEach(item => {
      const raw = parseTechnologyProductName(item.value);
      productDisplayMap[raw] = formatPrettyName(raw);
    });
    productDisplayMapInverse = invertMap(productDisplayMap);

    return Object.values(productDisplayMap).sort((left, right) =>
      left.localeCompare(right)
    );
  } catch {
    return [];
  }
}

/**
 * Technology Product filter definition
 * Dropdown with dynamically fetched product values
 */
export const TechnologyProduct: SimpleFilterDefinition = {
  apiField: 'product',
  uiField: 'Product',
  inputType: 'dropdown',
  getAvailableValues: fetchTechnologyProducts,
  operators: ['is', 'is not', 'is any of', 'is none of'],

  translateForUI: (queryFilter: QueryFilter): SimpleFilter => {
    const operator = getOperatorFromQueryFilter(queryFilter);
    const unpacked = unpackStringsForUI(queryFilter.value, operator);

    // Map API codes to display names using helper
    const uiValue = getValueFromQueryFilter(productDisplayMap, {
      ...queryFilter,
      value: unpacked,
    });

    return {
      field: 'Product',
      operator,
      value: uiValue,
    };
  },

  translateForAPI: (simpleFilter: SimpleFilter): QueryFilter => {
    // Map display names to API codes using helper
    const mappedValue = getValueFromSimpleFilter(
      productDisplayMapInverse,
      simpleFilter
    );

    // Then pack for API
    const apiValue = packStringsForAPI(
      mappedValue as string | string[],
      simpleFilter.operator
    );

    return {
      field: 'product',
      operator: getOperatorFromSimpleFilter(simpleFilter),
      value: apiValue,
      not: getNotFromSimpleFilter(simpleFilter),
    };
  },
};
